import { useMemo } from 'react'

import { Chip, Table, Tabs } from '@heroui/react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { useVehicleAnalyticsStore } from '@/store/vehicleAnalytics.store'
import type { VehicleCostEntry } from '@/types/vehicleAnalytics'
import { formatPLN } from '@/utils/currency'
import { formatDate } from '@/utils/dates'

type TabKey = 'advance' | 'paidRoads' | 'fuel' | 'excluded';

interface EntriesTableProps {
  entries: VehicleCostEntry[];
  ariaLabel: string;
  labelHeader?: string;
  showLiters?: boolean;
}

function EntriesTable({ entries, ariaLabel, labelHeader, showLiters }: EntriesTableProps) {
  const { t } = useTranslation()
  const total = entries.reduce((s, e) => s + e.amountPLN, 0)

  return (
    <Table>
      <Table.ScrollContainer className="max-h-[480px]">
        <Table.Content aria-label={ariaLabel} className="min-w-[700px]">
          <Table.Header>
            <Table.Column isRowHeader>{t('common.date')}</Table.Column>
            {labelHeader ? <Table.Column>{labelHeader}</Table.Column> : null}
            {showLiters ? <Table.Column>{t('reports.table.liters')}</Table.Column> : null}
            <Table.Column>{t('reports.table.country')}</Table.Column>
            <Table.Column>{t('reports.driver')}</Table.Column>
            <Table.Column>{t('vehicle_analytics.report')}</Table.Column>
            <Table.Column>{t('reports.table.pln_amount')}</Table.Column>
          </Table.Header>
          <Table.Body>
            {entries.map(entry => (
              <Table.Row key={`${entry.reportId}-${entry.id}`}>
                <Table.Cell>{formatDate(entry.date)}</Table.Cell>
                {labelHeader ? (
                  <Table.Cell>
                    {entry.fuelType ? t(`reports.fuel_types.${entry.fuelType}`, entry.fuelType) : entry.label}
                  </Table.Cell>
                ) : null}
                {showLiters ? (
                  <Table.Cell className="tabular-nums">{formatPLN(entry.liters)} L</Table.Cell>
                ) : null}
                <Table.Cell>{entry.country ?? '-'}</Table.Cell>
                <Table.Cell>{entry.driverName ?? '-'}</Table.Cell>
                <Table.Cell>
                  <Link to={`/reports/${entry.reportId}`} className="text-accent hover:underline">
                    {entry.reportNumber ? `#${entry.reportNumber}` : t('vehicle_analytics.open_report')}
                  </Link>
                </Table.Cell>
                <Table.Cell className="font-bold tabular-nums">{formatPLN(entry.amountPLN)} PLN</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Content>
      </Table.ScrollContainer>
      <Table.Footer>
        <div className="flex w-full justify-end py-2">
          <div className="text-right">
            <div className="text-xs font-black text-zinc-600 dark:text-zinc-400">{t('vehicle_analytics.total')}</div>
            <div className="text-lg font-black tracking-tight tabular-nums">{formatPLN(total)} PLN</div>
          </div>
        </div>
      </Table.Footer>
    </Table>
  )
}

export function CostEntriesTabs() {
  const { t } = useTranslation()
  const { entries, excludedAdvanceFuel } = useVehicleAnalyticsStore()

  const byCategory = useMemo(() => ({
    advance: entries.filter(e => e.category === 'advance'),
    paidRoads: entries.filter(e => e.category === 'paidRoads'),
    fuel: entries.filter(e => e.category === 'fuel'),
  }), [entries])

  const tabs: { id: TabKey; label: string; count: number }[] = [
    { id: 'advance', label: t('vehicle_analytics.categories.advance'), count: byCategory.advance.length },
    { id: 'paidRoads', label: t('vehicle_analytics.categories.paidRoads'), count: byCategory.paidRoads.length },
    { id: 'fuel', label: t('vehicle_analytics.categories.fuel'), count: byCategory.fuel.length },
    { id: 'excluded', label: t('vehicle_analytics.excluded_fuel'), count: excludedAdvanceFuel.length },
  ]

  return (
    <Tabs className="w-full" variant={'secondary'} defaultSelectedKey="advance">
      <Tabs.ListContainer>
        <Tabs.List aria-label={t('vehicle_analytics.entries')} className="w-fit">
          {tabs.map(tab => (
            <Tabs.Tab key={tab.id} id={tab.id} isDisabled={tab.count === 0} className="gap-3 px-4 min-w-fit whitespace-nowrap">
              {tab.label}
              <Chip className="ml-auto h-5" color={tab.id === 'excluded' && tab.count > 0 ? 'warning' : 'default'}>
                {tab.count}
              </Chip>
              <Tabs.Indicator />
            </Tabs.Tab>
          ))}
        </Tabs.List>
      </Tabs.ListContainer>

      <Tabs.Panel className="pt-4" id="advance">
        <EntriesTable entries={byCategory.advance} ariaLabel={tabs[0].label} labelHeader={t('reports.table.type')} />
      </Tabs.Panel>
      <Tabs.Panel className="pt-4" id="paidRoads">
        <EntriesTable entries={byCategory.paidRoads} ariaLabel={tabs[1].label} />
      </Tabs.Panel>
      <Tabs.Panel className="pt-4" id="fuel">
        <EntriesTable entries={byCategory.fuel} ariaLabel={tabs[2].label} labelHeader={t('reports.table.fuel_type')} showLiters />
      </Tabs.Panel>
      <Tabs.Panel className="pt-4 space-y-3" id="excluded">
        <p className="text-sm text-foreground/60">{t('vehicle_analytics.excluded_fuel_hint')}</p>
        <EntriesTable entries={excludedAdvanceFuel} ariaLabel={tabs[3].label} labelHeader={t('reports.table.type')} />
      </Tabs.Panel>
    </Tabs>
  )
}
